/**
 * Independent & Dependent Variables generator (6.PAFR.2.4).
 *
 * Multiple-choice questions asking the student to identify the independent
 * (input) or dependent (output) variable in a real-world relationship or an
 * equation of the form y = m*x. The correct variable and its role are decided
 * first, and the two variables become the choice options, so the graded choice
 * id is always one of the presented options.
 *
 * Families:
 *   F1: identify the dependent variable (the output)
 *   F2: identify the independent variable (the input)
 *   F3: identify the dependent variable from a table's columns
 */

import { rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Choice,
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "independent-dependent" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

/**
 * A relationship where `dep` depends on `ind`. `depVar`/`indVar` are the
 * equation symbols; `depName`/`indName` are the words used in the prompt.
 */
interface Relationship {
  readonly depVar: string;
  readonly indVar: string;
  readonly depName: string;
  readonly indName: string;
  readonly describe: (rate: string) => string;
}

const RELATIONSHIPS: readonly Relationship[] = [
  {
    depVar: "d", indVar: "t", depName: "distance", indName: "time",
    describe: (r) => `A car travels at a steady speed, so distance depends on time: d = ${r}t.`,
  },
  {
    depVar: "c", indVar: "n", depName: "cost", indName: "number of items",
    describe: (r) => `Each item costs the same, so total cost depends on the number of items: c = ${r}n.`,
  },
  {
    depVar: "p", indVar: "h", depName: "pay", indName: "hours worked",
    describe: (r) => `A worker earns a fixed hourly wage, so pay depends on hours worked: p = ${r}h.`,
  },
  {
    depVar: "w", indVar: "d", depName: "water used", indName: "days",
    describe: (r) => `A tank fills at a steady rate, so water used depends on the number of days: w = ${r}d.`,
  },
];

const DIFF: Record<Difficulty, readonly Family[]> = {
  easy: ["F1", "F2"],
  standard: ["F1", "F2", "F3"],
  hard: ["F1", "F2", "F3"],
  challenge: ["F1", "F2", "F3"],
};

export class IndependentDependentGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const families = DIFF[ctx.difficulty];
      const family = rng.pick(families);
      const rel = rng.pick(RELATIONSHIPS);
      const rate = rat(rng.int(2, 60));

      const wantDependent = family !== "F2";

      let promptText: string;
      let promptLatex: string | undefined;
      let options: { id: string; latex: string; correct: boolean }[];
      let explanation: string;

      if (family === "F3") {
        // Table framing: two columns, the second depends on the first.
        promptText = `A table lists ${rel.indName} in the first column and ${rel.depName} in the second column, where ${rel.depName} is calculated from ${rel.indName}. Which is the dependent variable?`;
        promptLatex = `${rel.depVar} = ${rate.toLatex()}${rel.indVar}`;
        options = [
          { id: "dep", latex: `\\text{${rel.depName} (${rel.depVar})}`, correct: true },
          { id: "ind", latex: `\\text{${rel.indName} (${rel.indVar})}`, correct: false },
        ];
        explanation = `The value computed from the other is the dependent variable: ${rel.depName}.`;
      } else {
        promptText = `${rel.describe(rate.toString())} Which is the ${wantDependent ? "dependent" : "independent"} variable?`;
        promptLatex = `${rel.depVar} = ${rate.toLatex()}${rel.indVar}`;
        options = [
          { id: "dep", latex: `\\text{${rel.depName} (${rel.depVar})}`, correct: wantDependent },
          { id: "ind", latex: `\\text{${rel.indName} (${rel.indVar})}`, correct: !wantDependent },
        ];
        explanation = wantDependent
          ? `${rel.depName} changes in response to ${rel.indName}, so ${rel.depName} is dependent.`
          : `${rel.indName} is the input you choose freely, so ${rel.indName} is independent.`;
      }

      const shuffled = rng.shuffle(options);
      const choices: Choice[] = shuffled.map((o) => ({ id: o.id, latex: o.latex }));
      const correct = shuffled.find((o) => o.correct);
      if (!correct) continue;

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText, latex: promptLatex },
        answerType: "choice",
        answer: { kind: "choice", value: correct.id },
        steps: [{ description: explanation, latex: promptLatex ?? "" }],
        hints: [
          { level: 1, text: "The independent variable is the input; the dependent variable is the output it produces." },
          { level: 2, text: "In y = mx, x is independent (chosen) and y depends on x." },
        ],
        subskills: topic.subskills,
        choices,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "choice" ? q.answer.value : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const independentDependent = new IndependentDependentGenerator();
